import { channelPathsToEntries, entriesToChannelPaths } from './channelOptions'
import {
  COLLECT_MODE_HISTORY,
  COLLECT_MODE_REALTIME,
  buildDateTimeRange,
  toApiDateTime
} from './taskFormUtils'

export const MISSING_FIELD_LABELS = {
  taskName: '任务名称',
  channels: '渠道',
  collectMode: '采集模式',
  startTime: '开始时间',
  endTime: '结束时间',
  keywordGroups: '关键词方案'
}

export function emptyApplyDraft() {
  return {
    taskName: '',
    channels: [],
    collectMode: '',
    startTime: '',
    endTime: '',
    monitorObject: '',
    monitorHint: '',
    keywordGroups: [],
    excludeWords: [],
    keywordsConfirmed: false,
    collectPosts: true,
    collectComments: false,
    collectInteractions: false,
    commentUpdateDays: 0,
    interactionUpdateDays: 0,
    applyReason: '',
    blockLimit: ''
  }
}

export function normalizeApplyDraft(draft) {
  const source = draft && typeof draft === 'object' ? draft : {}
  const base = emptyApplyDraft()
  return {
    ...base,
    ...source,
    taskName: trimText(source.taskName),
    channels: Array.isArray(source.channels) ? source.channels.slice() : [],
    collectMode: trimText(source.collectMode),
    startTime: trimText(source.startTime),
    endTime: trimText(source.endTime),
    monitorObject: trimText(source.monitorObject),
    monitorHint: trimText(source.monitorHint),
    keywordGroups: normalizeKeywordGroups(source.keywordGroups),
    excludeWords: Array.isArray(source.excludeWords)
      ? source.excludeWords.map(trimText).filter(Boolean)
      : [],
    keywordsConfirmed: source.keywordsConfirmed === true,
    collectPosts: true,
    collectComments: source.collectComments === true,
    collectInteractions: source.collectInteractions === true,
    commentUpdateDays: source.commentUpdateDays == null ? 0 : source.commentUpdateDays,
    interactionUpdateDays: source.interactionUpdateDays == null ? 0 : source.interactionUpdateDays,
    applyReason: trimText(source.applyReason),
    blockLimit: source.blockLimit == null ? '' : source.blockLimit
  }
}

export function computeMissingFields(draft) {
  const current = normalizeApplyDraft(draft)
  const missing = []
  if (!current.taskName) missing.push('taskName')
  if (!hasValidChannels(current.channels)) missing.push('channels')
  if (!isAllowedCollectMode(current.collectMode)) missing.push('collectMode')
  if (!current.startTime) missing.push('startTime')
  if (!current.endTime) missing.push('endTime')
  if (!current.keywordsConfirmed || !hasKeywordWords(current.keywordGroups)) {
    missing.push('keywordGroups')
  }
  return missing
}

export function isApplyDraftReady(draft) {
  return computeMissingFields(draft).length === 0
}

export function mapDraftToForm(draft, defaultForm, newKeywordGroup) {
  const current = normalizeApplyDraft(draft)
  const groups = current.keywordGroups
    .map(group => newKeywordGroup(group.words))
    .filter(group => group.words.length > 0)
  return {
    ...defaultForm,
    taskName: current.taskName,
    channelPaths: entriesToChannelPaths(current.channels),
    collectMode: isAllowedCollectMode(current.collectMode) ? current.collectMode : defaultForm.collectMode,
    dateRange: current.startTime && current.endTime
      ? buildDateTimeRange(current.startTime, current.endTime)
      : [],
    keywordGroups: groups.length ? groups : [newKeywordGroup()],
    excludeWords: current.excludeWords.slice(),
    collectPosts: true,
    collectComments: current.collectComments,
    collectInteractions: current.collectInteractions,
    commentUpdateDays: current.commentUpdateDays,
    interactionUpdateDays: current.interactionUpdateDays,
    applyReason: current.applyReason,
    blockLimit: current.blockLimit
  }
}

export function channelsFromPaths(paths) {
  return channelPathsToEntries(paths)
}

export function formatDraftPeriod(draft) {
  const current = normalizeApplyDraft(draft)
  if (!current.startTime || !current.endTime) return ''
  const range = buildDateTimeRange(current.startTime, current.endTime)
  return range.length === 2 ? `${range[0]} ~ ${range[1]}` : ''
}

export function formatDraftChannels(draft) {
  const current = normalizeApplyDraft(draft)
  if (!current.channels.length) return ''
  return current.channels.map(item => item.name || item.media || item.pubCode).filter(Boolean).join('、')
}

export function formatDraftKeywords(draft) {
  const current = normalizeApplyDraft(draft)
  if (!hasKeywordWords(current.keywordGroups)) return ''
  return current.keywordGroups
    .map(group => '（' + (group.words || []).join(' 或 ') + '）')
    .join(' 且 ')
}

/**
 * 申请辅助量级卡与申请表共用同一套预估入参。
 * channel 为 entries 数组的 JSON，与 encodeChannelJson(entriesToChannelPaths) 同类。
 */
export function buildEstimatePayloadFromDraft(draft) {
  const current = normalizeApplyDraft(draft)
  const keywords = current.keywordGroups
    .map(group => (group.words || []).map(word => String(word).trim()).filter(Boolean))
    .filter(words => words.length > 0)
    .map((words, index) => ({
      groupNo: index + 1,
      relation: 'OR',
      words
    }))
  return {
    taskName: current.taskName,
    channel: JSON.stringify(current.channels),
    collectMode: current.collectMode,
    startTime: toApiDateTime(current.startTime),
    endTime: toApiDateTime(current.endTime),
    keywordsJson: JSON.stringify(keywords),
    excludeWordsJson: JSON.stringify(current.excludeWords.slice()),
    collectPosts: current.collectPosts,
    collectComments: current.collectComments,
    collectInteractions: current.collectInteractions,
    commentUpdateDays: current.commentUpdateDays,
    interactionUpdateDays: current.interactionUpdateDays,
    applyReason: current.applyReason,
    blockLimit: current.blockLimit !== '' && current.blockLimit != null
      ? Number(current.blockLimit)
      : null
  }
}

function normalizeKeywordGroups(groups) {
  if (!Array.isArray(groups)) return []
  return groups.map(group => {
    if (Array.isArray(group)) {
      return { words: group.map(trimText).filter(Boolean) }
    }
    const words = group && Array.isArray(group.words) ? group.words : []
    return { words: words.map(trimText).filter(Boolean) }
  }).filter(group => group.words.length > 0)
}

function hasKeywordWords(groups) {
  return normalizeKeywordGroups(groups).some(group => group.words.length > 0)
}

function hasValidChannels(channels) {
  if (!Array.isArray(channels) || !channels.length) return false
  return channels.every(item => item && trimText(item.pubCode))
}

function isAllowedCollectMode(value) {
  return value === COLLECT_MODE_HISTORY || value === COLLECT_MODE_REALTIME
}

function trimText(value) {
  return String(value == null ? '' : value).trim()
}
